import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { graphBuilderApi } from '../../services/api';
import { getCurrentUser } from '../../utils/roleCheck';
import './CDIDemoPage.css';

const CDI_PROCESS_ID = 'fb6c1ce9-81c0-44fd-aa33-524093b49a2a';

const SAMPLE_CASES = {
  sample_57: {
    label: 'Sample Case: Syncope (84yo Male)',
    description: '84-year-old male, syncope admission, 6 material CDI findings',
    case_text: `84-year-old male admitted via ED for syncope. History of hypertension, anemia, and chronic kidney disease stage III. On admission, patient was found to have possible aspiration pneumonitis based on imaging showing bilateral lower lobe infiltrates. Swallow study performed on day 2 was negative for aspiration. Urinalysis showed bacteriuria but patient was asymptomatic -- documentation states 'UTI' but contradicts NHSN surveillance criteria for symptomatic UTI. Lab trends: Creatinine 2.31 on admission (day 1), 2.46 (day 2), 2.30 (day 3), 2.50 (day 4), 2.63 (day 5), 2.76 (day 6) -- baseline creatinine from 3 months prior was 1.8. Urine protein 4.2g/24h with serum albumin 2.8 g/dL consistent with nephrotic-range proteinuria. BNP 890 pg/mL, echocardiogram shows EF 55% with diastolic dysfunction grade II, consistent with HFpEF. Documentation states 'hypertension' (I10) but echo and BNP suggest hypertensive heart disease with heart failure (I11.0). Hemoglobin 9.2 g/dL, iron studies show low ferritin consistent with iron deficiency anemia, but current coding shows D64.9 (unspecified anemia). Discharge diagnosis: syncope (R55), hypertension (I10), unspecified anemia (D64.9).`,
    current_drg: '312',
    coded_conditions: ['R55', 'I10', 'D64.9'],
    admission_date: '2025-01-02',
    discharge_date: '2025-01-07',
    facility_base_rate: 9116,
    expected: {
      pdx_change: { from: 'R55', to: 'J69.0', from_desc: 'Syncope and Collapse', to_desc: 'Aspiration Pneumonitis' },
      pre_review: { drg: '312', title: 'Syncope and Collapse', weight: 0.8716 },
      post_review: { drg: '178', title: 'Respiratory Infections & Inflammations with CC', weight: 0.9760 },
      codes_added: ['J69.0', 'N17.9', 'I11.0', 'I50.32', 'N04.9', 'D50.9'],
      codes_deleted: ['I10', 'D64.9'],
      queries_expected: 4,
    },
    expert_findings: [
      {
        condition: 'Aspiration Pneumonitis',
        icd10_code: 'J69.0',
        finding_type: 'query',
        action: 'Query',
        qualifier: 'possible',
        description: 'Query whether documented aspiration pneumonitis was one of the conditions prompting the inpatient admission. Evidence includes ED impression, CT findings showing bilateral lower lobe infiltrates, history of aspiration pneumonia, H&P documentation, continued antibiotics, and discharge-summary documentation. The modified barium swallow did not show aspiration during this encounter but did not eliminate the documented clinical concern.',
        materiality: 'If confirmed as a condition prompting admission, aspiration pneumonitis (J69.0) replaces syncope (R55) as the principal diagnosis under ICD-10-CM Section II sequencing rules. This resequences the DRG from 312 (Syncope, weight 0.8716) to 178 (Respiratory Infections with CC, weight 0.9760), increasing estimated reimbursement by ~$951.',
        sources: ['snn_aspiration_pneumonia', 'snn_icd10cm_guidelines', 'snn_msdrg_definitions'],
      },
      {
        condition: 'Hypertensive Heart Disease with Heart Failure',
        icd10_code: 'I11.0',
        finding_type: 'direct',
        action: 'Upgrade',
        qualifier: 'confirmed',
        description: 'Report chronic HFpEF as documented and apply the combination code for hypertensive heart disease with heart failure. BNP 890 pg/mL and echocardiogram showing EF 55% with grade II diastolic dysfunction confirm HFpEF. Per ICD-10-CM guidelines, hypertension with heart failure requires the combination code I11.0 — the standalone I10 must be replaced.',
        materiality: 'I11.0 is a CC/MCC-level code that captures both the hypertension and the heart failure in a single combination code. Coding I10 alone misses the heart failure entirely, which is both a compliance risk (incomplete code capture) and a DRG risk (I10 carries no CC/MCC weight; I11.0 does).',
        sources: ['snn_heart_failure', 'snn_icd10cm_guidelines', 'snn_cc_mcc_matrix'],
      },
      {
        condition: 'Nephrotic Syndrome',
        icd10_code: 'N04.9',
        finding_type: 'query',
        action: 'Query',
        qualifier: 'possible',
        description: 'Query to clarify documented nephrotic-range proteinuria because the clinical indicators suggest possible nephrotic syndrome. Urine protein 4.2 g/24h with serum albumin 2.8 g/dL meets laboratory thresholds for nephrotic-range proteinuria, but nephrotic syndrome requires physician confirmation of the clinical diagnosis.',
        materiality: 'Nephrotic syndrome (N04.9) is an MCC that significantly impacts DRG weight. Without the query, this condition cannot be coded — the proteinuria alone is insufficient for a diagnosis of nephrotic syndrome. If confirmed, this adds an MCC to the case.',
        sources: ['snn_nephrotic_syndrome', 'snn_clinical_indicators'],
      },
      {
        condition: 'UTI vs Asymptomatic Bacteriuria',
        icd10_code: 'N39.0',
        finding_type: 'query',
        action: 'Query',
        qualifier: 'possible',
        description: 'Query to confirm the diagnosis of UTI because the clinical indicators suggest that it may have been ruled out. The patient is documented as asymptomatic with bacteriuria only — this contradicts CDC/NHSN surveillance criteria for symptomatic UTI. Documentation states "UTI" but the clinical picture supports asymptomatic bacteriuria, not infection.',
        materiality: 'If UTI is not clinically supported, coding N39.0 creates a denial risk — payers routinely audit UTI claims for NHSN compliance. Querying protects the facility from post-discharge denials. If ruled out, the UTI code should be removed to maintain coding accuracy.',
        sources: ['snn_uti_nhsn', 'snn_governance', 'snn_query_compliance'],
      },
      {
        condition: 'Acute Kidney Injury — POA Clarification',
        icd10_code: 'N17.9',
        finding_type: 'query',
        action: 'Query',
        qualifier: 'confirmed',
        description: 'Query to confirm the POA status of AKI. Although AKI was documented on admission with creatinine 2.31 (baseline 1.8), the creatinine trend was interpreted as not meeting full KDIGO criteria until day 6 (creatinine 2.76). This supports POA status N — the AKI worsened to clinical significance after admission.',
        materiality: 'AKI with POA=N (not present on admission) qualifies as a hospital-acquired complication. Correct POA determination is a CMS compliance requirement. Additionally, N17.9 is a CC that contributes to DRG weight — accurate POA status ensures the condition is properly captured without audit risk.',
        sources: ['snn_kdigo_aki', 'snn_kdigo_aki_supplement', 'snn_poa_reporting'],
      },
      {
        condition: 'Anemia in Chronic Kidney Disease',
        icd10_code: 'D63.1',
        finding_type: 'direct',
        action: 'Upgrade',
        qualifier: 'confirmed',
        description: 'Report anemia in CKD under the applicable ICD-10-CM relationship and coding rules. Hemoglobin 9.2 g/dL with low ferritin in the context of CKD III requires the anemia-in-CKD code (D63.1) rather than unspecified anemia (D64.9). ICD-10-CM coding guidelines mandate specificity when the clinical documentation supports it.',
        materiality: 'D64.9 (unspecified anemia) carries no CC/MCC weight. D63.1 (anemia in CKD) is a CC that captures the causal relationship and increases code specificity. This is both a compliance improvement (coding to highest specificity) and a DRG impact (adds CC weight to the case).',
        sources: ['snn_icd10cm_guidelines', 'snn_icd10cm_tabular', 'snn_clinical_indicators'],
      },
    ],
  },
  case_b_sepsis: {
    label: 'Sample Case: Sepsis (72yo Female)',
    description: '72-year-old female, sepsis with multi-organ dysfunction, 7 conditions, 3 competing DRG paths',
    case_text: `72-year-old female admitted from ED with fever 39.2C, tachycardia HR 112, hypotension BP 82/54 requiring norepinephrine infusion. Blood cultures positive for E. coli. Lactate 4.2 mmol/L on admission, repeat lactate 2.8 after fluid resuscitation. SOFA score calculated: cardiovascular 3 (vasopressors), renal 2 (creatinine 2.4 from baseline 0.9), hepatic 1 (bilirubin 1.8), coagulation 1 (platelets 128K), respiratory 2 (PaO2/FiO2 220 on 4L NC), neurological 1 (GCS 14, mildly confused). Urine culture positive for E. coli >100K CFU/mL -- source identified as urosepsis. Patient intubated on day 2 for respiratory failure, extubated day 7 (approximately 120 hours mechanical ventilation). Creatinine trend: 2.4 admission, 3.1 day 2, 3.8 day 3, 2.9 day 5, 2.2 day 8 -- consistent with KDIGO Stage 2 AKI (>2x baseline). Albumin 2.1 g/dL, BMI 17.8, unintentional weight loss 12% over 6 months, MUST score 4 -- meets GLIM criteria for severe malnutrition. Documentation notes 'altered mental status likely metabolic encephalopathy vs septic.' History of COPD on home O2 2L -- acute hypoxic respiratory failure superimposed on chronic. Ammonia level normal at 42 mcg/dL, ruling out hepatic encephalopathy. LOS 14 days, discharge to SNF.`,
    current_drg: '872',
    coded_conditions: ['A41.51', 'N39.0', 'J44.1'],
    admission_date: '2025-02-10',
    discharge_date: '2025-02-24',
    facility_base_rate: 9116,
    expected: {
      pdx_change: null,
      pre_review: { drg: '872', title: 'Septicemia or Severe Sepsis w/o MV >96 Hours w/o MCC', weight: 1.2637 },
      post_review: { drg: '870', title: 'Septicemia or Severe Sepsis with MV >96 Hours', weight: 5.1688 },
      codes_added: ['R65.21', '5A1955Z', 'N17.9', 'E43', 'G93.41', 'J96.00'],
      codes_deleted: [],
      queries_expected: 0,
    },
    expert_findings: [
      {
        condition: 'Sepsis with Septic Shock',
        icd10_code: 'A41.51 + R65.21',
        finding_type: 'direct',
        action: 'Add',
        qualifier: 'confirmed',
        description: 'Sepsis-3 criteria fully met: documented infection (E. coli bacteremia), SOFA score 10 (≥2 above baseline). Septic shock confirmed by vasopressor requirement (norepinephrine) with lactate 4.2 mmol/L (>2). Code A41.51 for sepsis due to E. coli with R65.21 for severe sepsis with septic shock.',
        materiality: 'Sepsis with septic shock is the principal diagnosis driver for the DRG 870/871/872 pathway. Without explicit sepsis coding, the case groups to a lower-weight medical DRG. This is the single highest-impact finding — DRG 870 carries weight 5.1688 (~$47,118 estimated payment).',
        sources: ['snn_sepsis3', 'snn_ssc_guidelines', 'snn_msdrg_sepsis_logic'],
      },
      {
        condition: 'Mechanical Ventilation >96 Hours',
        icd10_code: '5A1955Z',
        finding_type: 'direct',
        action: 'Add',
        qualifier: 'confirmed',
        description: 'Patient intubated day 2, extubated day 7 — approximately 120 hours of mechanical ventilation. This exceeds the 96-hour threshold that differentiates DRG 870 from 871.',
        materiality: 'The 96-hour MV threshold is the critical DRG decision point: DRG 870 (MV >96h, weight 5.1688) vs DRG 871 (MV ≤96h, weight 1.8782). Accurate MV duration documentation drives a weight difference of 3.2906, representing ~$29,990 in estimated reimbursement.',
        sources: ['snn_pcs_guidelines', 'snn_msdrg_sepsis_logic'],
      },
      {
        condition: 'Acute Kidney Injury — KDIGO Stage 2',
        icd10_code: 'N17.9',
        finding_type: 'direct',
        action: 'Add',
        qualifier: 'confirmed',
        description: 'Creatinine 3.8 on day 3 represents a 4.2× increase from baseline 0.9, meeting KDIGO Stage 2 criteria (>2× baseline). AKI developed after admission (POA=N) and resolved partially by discharge (creatinine 2.2 on day 8).',
        materiality: 'AKI is an MCC that contributes to DRG severity. In the sepsis pathway, MCCs are already captured, but accurate AKI documentation supports the overall acuity picture and protects against payer challenges to the DRG assignment.',
        sources: ['snn_kdigo_aki', 'snn_kdigo_aki_supplement', 'snn_poa_reporting'],
      },
      {
        condition: 'Severe Malnutrition — GLIM Criteria',
        icd10_code: 'E43',
        finding_type: 'direct',
        action: 'Add',
        qualifier: 'confirmed',
        description: 'GLIM criteria for severe malnutrition fully met: BMI 17.8 (<18.5), albumin 2.1 g/dL, unintentional weight loss 12% over 6 months (>10%), MUST score 4. Documentation supports severe protein-calorie malnutrition.',
        materiality: 'Severe malnutrition (E43) is an MCC. In the sepsis DRG pathway, this strengthens the severity-of-illness documentation. Additionally, malnutrition is a CMS quality measure — accurate capture supports both reimbursement and quality reporting.',
        sources: ['snn_glim_malnutrition', 'snn_espen_glim', 'snn_clinical_indicators'],
      },
      {
        condition: 'Metabolic Encephalopathy',
        icd10_code: 'G93.41',
        finding_type: 'direct',
        action: 'Add',
        qualifier: 'confirmed',
        description: 'Documentation notes altered mental status with metabolic encephalopathy. Normal ammonia (42 mcg/dL) rules out hepatic encephalopathy. GCS 14 supports the diagnosis. Metabolic encephalopathy in the setting of sepsis is a recognized complication.',
        materiality: 'G93.41 is an MCC that captures the neurological component of multi-organ dysfunction. Accurate coding of encephalopathy supports the clinical picture of sepsis-related organ failure and strengthens the medical necessity documentation.',
        sources: ['snn_clinical_indicators', 'snn_icd10cm_guidelines'],
      },
      {
        condition: 'Acute Respiratory Failure',
        icd10_code: 'J96.00',
        finding_type: 'direct',
        action: 'Add',
        qualifier: 'confirmed',
        description: 'PaO2/FiO2 ratio 220 on admission (SOFA respiratory score 2), requiring intubation on day 2. Acute hypoxic respiratory failure superimposed on chronic COPD. Documentation supports acute-on-chronic respiratory failure requiring mechanical ventilation.',
        materiality: 'Acute respiratory failure (J96.00) is an MCC and directly supports the mechanical ventilation requirement. This finding strengthens the medical necessity for intubation and the >96-hour MV duration that drives DRG 870.',
        sources: ['snn_icd10cm_guidelines', 'snn_pcs_guidelines'],
      },
    ],
  },
};

const QUALIFIER_COLORS = {
  confirmed: '#16a34a',
  possible: '#d97706',
  query: '#d97706',
  delete: '#dc2626',
  ruled_out: '#dc2626',
  upgrade: '#0891b2',
};

const GRAPH_DISPLAY_NAME = {
  snn_mce_v42: 'Medicare Code Editor v42',
  snn_icd10cm_tabular: 'ICD-10-CM Tabular Index',
  snn_icd10cm_guidelines: 'ICD-10-CM Official Guidelines',
  snn_msdrg_definitions: 'MS-DRG Definitions Manual',
  snn_msdrg_appendix_c: 'MS-DRG CC/MCC Exclusions',
  snn_msdrg_sepsis_logic: 'MS-DRG Sepsis Pathway Logic',
  snn_sepsis3: 'Sepsis-3 Clinical Criteria',
  snn_ssc_guidelines: 'Surviving Sepsis Campaign Guidelines',
  snn_kdigo_aki: 'KDIGO AKI Staging Criteria',
  snn_kdigo_aki_supplement: 'KDIGO AKI Supplemental',
  snn_glim_malnutrition: 'GLIM Malnutrition Criteria',
  snn_espen_glim: 'ESPEN GLIM Framework',
  snn_heart_failure: 'Heart Failure Classification',
  snn_aspiration_pneumonia: 'Aspiration Pneumonitis vs Pneumonia',
  snn_aspiration_index: 'Aspiration Risk Index',
  snn_uti_nhsn: 'NHSN UTI Surveillance Criteria',
  snn_pcs_guidelines: 'PCS Procedure Coding Guidelines',
  snn_poa_reporting: 'POA Indicator Reporting Guidelines',
  snn_query_compliance: 'AHIMA/ACDIS Query Compliance',
  snn_governance: 'CDI Governance Standards',
  snn_case_b_supplement: 'Case Supplement',
  snn_nephrotic_syndrome: 'Nephrotic Syndrome Criteria',
  snn_coding_clinics: 'AHA Coding Clinic',
  snn_psi_definitions: 'PSI/HAC Definitions',
  snn_clinical_indicators: 'Clinical Indicators',
  snn_cc_mcc_matrix: 'CC/MCC Severity Matrix',
};

const CONFIDENCE_COLOR = (score) => {
  if (score >= 0.8) return '#16a34a';
  if (score >= 0.5) return '#d97706';
  return '#dc2626';
};

function safeParse(v) {
  if (!v) return v;
  if (typeof v === 'string') { try { return JSON.parse(v); } catch { return v; } }
  return v;
}

function extractCortexData(nodeResults, statusData) {
  const nr = nodeResults || {};
  const ctx = statusData?.context || {};

  const capEngOp = safeParse(nr.capability_engines?.outputs_produced) || {};
  const crossOp = safeParse(nr.cross_reasoning?.outputs_produced) || {};
  const caseAssemblyOp = safeParse(nr.case_file_assembly?.outputs_produced) || {};
  const cortexActOp = safeParse(nr.cortex_activation?.outputs_produced) || {};

  // Conditions from context (primary source in 5-phase pipeline)
  const conditionsEvaluated = ctx.conditions_identified || [];

  // Coding recs / physician queries: try capability_engines/case_file_assembly outputs,
  // then fall back to deriving from conditions
  const codingRecs = capEngOp.coding_recommendations
    || caseAssemblyOp.coding_recommendations || [];
  const physicianQueries = capEngOp.physician_queries
    || caseAssemblyOp.physician_queries || [];

  // DRG impact: try structured output first, then context fields
  let drgImpact = capEngOp.drg_impact || caseAssemblyOp.drg_impact || {};
  if (!drgImpact.current_drg && ctx.current_drg) {
    drgImpact = { ...drgImpact, current_drg: ctx.current_drg };
  }

  // Build lookup maps from explicit arrays
  const recsByCondition = {};
  codingRecs.forEach((r) => { if (r.condition) recsByCondition[r.condition] = r; });
  const queryByCondition = {};
  physicianQueries.forEach((q) => { if (q.condition) queryByCondition[q.condition] = q; });

  // Also build recs/queries from conditions themselves when arrays are empty
  conditionsEvaluated.forEach((c) => {
    const name = c.condition || '';
    if (!recsByCondition[name] && c.action && c.action !== 'Retain') {
      recsByCondition[name] = {
        condition: name,
        recommendation: c.action === 'Query'
          ? `Query physician to clarify documentation for ${name} (${c.icd10_code || ''})`
          : c.action === 'Add'
          ? `Add ${c.icd10_code || ''} — ${name} supported by clinical evidence`
          : c.action === 'Upgrade'
          ? `Upgrade code specificity for ${name} to ${c.icd10_code || ''}`
          : c.action === 'Delete'
          ? `Remove ${name} — insufficient clinical support`
          : `${c.action}: ${name} (${c.icd10_code || ''})`,
        guideline_source: (c.source_graphs || []).map(g => GRAPH_DISPLAY_NAME[g] || g).join(', '),
      };
    }
    if (!recsByCondition[name] && c.action === 'Retain') {
      recsByCondition[name] = {
        condition: name,
        recommendation: `Retain ${c.icd10_code || ''} — ${name} supported by clinical evidence`,
        guideline_source: (c.source_graphs || []).map(g => GRAPH_DISPLAY_NAME[g] || g).join(', '),
      };
    }
    if (!queryByCondition[name] && (c.query_flag === true || c.query_or_direct === 'Q')) {
      queryByCondition[name] = {
        condition: name,
        query: `Physician query recommended: Please clarify documentation regarding ${name} to support accurate code assignment.`,
      };
    }
  });

  // Categorize SNN and capability nodes from top-level nodeResults
  const snnNodes = {};
  const capNodes = {};
  Object.entries(nr).forEach(([k, v]) => {
    if (k.startsWith('snn_')) snnNodes[k] = v;
    else if (k.startsWith('cap_')) capNodes[k] = v;
  });

  // Cortex activation data
  const cortexResult = safeParse(ctx.cortex_result) || cortexActOp || {};
  const firedNodes = cortexResult.fired_nodes || cortexResult.total_fired || 0;
  const rankedCaps = cortexResult.ranked_capabilities || {};
  const selectedCaps = Object.keys(rankedCaps);
  const selectedGraphs = Object.keys(snnNodes);

  // Token stats
  let totalTokens = 0;
  let totalCost = 0;
  Object.values(nr).forEach((n) => {
    if (n && typeof n === 'object') totalTokens += Number(n.tokens_used || 0);
  });
  const ctxUsage = ctx.llm_usage?.overall || {};
  if (ctxUsage.total_tokens) totalTokens = Number(ctxUsage.total_tokens);
  if (ctxUsage.cost_usd) totalCost = Number(ctxUsage.cost_usd);

  // Engine outputs live under outputs_produced.engine_outputs
  const engineOutputs = capEngOp.engine_outputs || {};

  // Governance from capability engines
  const govOp = safeParse(engineOutputs.cap_governance_enforcer) || {};
  const govStatus = govOp.governance_status || capEngOp.governance_status || '';
  const govGates = govOp.gates_passed || [];

  // Context-level summary data
  const score = ctx.score;
  const decision = ctx.decision;
  const gaps = ctx.gaps;

  // Materiality assessments from the engine
  const matEngine = safeParse(engineOutputs.cap_materiality_assessor) || {};
  const materialityAssessments = matEngine.materiality_assessments
    || capEngOp.materiality_assessments || [];

  return {
    conditionsEvaluated,
    codingRecs,
    physicianQueries,
    recsByCondition,
    queryByCondition,
    drgImpact,
    snnNodes,
    capNodes,
    selectedCaps,
    selectedGraphs,
    firedNodes,
    rankedCaps,
    totalTokens,
    totalCost,
    score,
    decision,
    gaps,
    governance: { governance_status: govStatus, gates_passed: govGates },
    materialityAssessments,
  };
}

function isMaterialFinding(c) {
  const action = (c.action || '').toLowerCase();
  const qualifier = (c.qualifier || '').toLowerCase();
  if (action && action !== 'retain') return true;
  if (qualifier === 'possible' || qualifier === 'query') return true;
  if (c.query_flag === true || c.query_or_direct === 'Q') return true;
  return false;
}

function CDIDemoPage() {
  const currentUser = getCurrentUser();
  const allowed = currentUser?.email === 'hpatelecom@yahoo3.com';

  const [caseText, setCaseText] = useState('');
  const [currentDrg, setCurrentDrg] = useState('');
  const [codedConditions, setCodedConditions] = useState('');
  const [facilityBaseRate, setFacilityBaseRate] = useState('9116');
  const [admissionDate, setAdmissionDate] = useState('');
  const [dischargeDate, setDischargeDate] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);

  const [executing, setExecuting] = useState(false);
  const [executionId, setExecutionId] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [nodeResults, setNodeResults] = useState(null);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const pollingRef = useRef(null);
  const timerRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  useEffect(() => { return () => stopPolling(); }, [stopPolling]);

  const cortexData = useMemo(
    () => extractCortexData(nodeResults, statusData),
    [nodeResults, statusData]
  );

  const loadSampleCase = (caseKey) => {
    const c = SAMPLE_CASES[caseKey];
    if (!c) return;
    setCaseText(c.case_text);
    setCurrentDrg(c.current_drg || '');
    setCodedConditions((c.coded_conditions || []).join(', '));
    setAdmissionDate(c.admission_date || '');
    setDischargeDate(c.discharge_date || '');
    setFacilityBaseRate(String(c.facility_base_rate || 9116));
    setSelectedCase(caseKey);
    setNodeResults(null);
    setStatusData(null);
    setError(null);
  };

  const handleAnalyze = async () => {
    if (!caseText.trim()) return;
    setError(null);
    setNodeResults(null);
    setStatusData(null);
    setExecuting(true);
    setElapsed(0);

    timerRef.current = setInterval(() => { setElapsed((p) => p + 1); }, 1000);

    try {
      const codes = codedConditions.split(',').map((s) => s.trim()).filter(Boolean);
      const payload = {
        process_id: CDI_PROCESS_ID,
        inputs: {
          case_text: caseText.trim(),
          coded_conditions: codes,
          current_drg: currentDrg.trim() || undefined,
          facility_base_rate: Number(facilityBaseRate) || 9116,
          fiscal_year: 'FY2025',
          admission_date: admissionDate || undefined,
          discharge_date: dischargeDate || undefined,
        },
        execution_mode: 'full',
      };

      const resp = await graphBuilderApi.executeProcess(payload);
      const execId = resp.execution_id;
      setExecutionId(execId);
      setStatusData(resp);

      pollingRef.current = setInterval(async () => {
        try {
          const st = await graphBuilderApi.getProcessStatus(execId);
          setStatusData(st);
          if (['completed', 'failed', 'cancelled', 'error'].includes(st.status)) {
            stopPolling();
            setExecuting(false);
            try {
              const nr = await graphBuilderApi.getNodeResults(execId);
              setNodeResults(nr);
            } catch (e) { console.error('Failed to fetch node results:', e); }
          }
        } catch (e) { console.error('Poll error:', e); }
      }, 1500);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to start execution');
      setExecuting(false);
      stopPolling();
    }
  };

  const completedNodes = statusData?.completed_nodes || [];
  const totalNodes = statusData?.total_node_count || 32;

  const getProgressLabel = () => {
    if (!statusData) return 'Initializing...';
    const pct = totalNodes > 0 ? Math.round((completedNodes.length / totalNodes) * 100) : 0;
    const n = completedNodes.length;
    if (n > 0 && n < 27) return `Evaluating clinical knowledge graphs... (${n}/${totalNodes}, ${pct}%)`;
    if (completedNodes.includes('cross_reasoning') && !completedNodes.includes('cortex_activation'))
      return `Cortex activation... (${n}/${totalNodes}, ${pct}%)`;
    if (completedNodes.includes('cortex_activation') && !completedNodes.includes('capability_engines'))
      return `Running capability engines... (${n}/${totalNodes}, ${pct}%)`;
    return `Processing... (${n}/${totalNodes}, ${pct}%)`;
  };

  if (!allowed) {
    return (
      <div className="cdi-demo-unauthorized">
        <h2>Access Denied</h2>
        <p>This demo is restricted. Please contact an administrator.</p>
      </div>
    );
  }

  const caseConfig = selectedCase ? SAMPLE_CASES[selectedCase] : null;
  const caseExpected = caseConfig?.expected || null;
  // Engine materiality assessments only — no hardcoded fallback
  const engineMat = cortexData.materialityAssessments;
  const displayFindings = engineMat.length > 0
    ? engineMat
    : cortexData.conditionsEvaluated.filter(isMaterialFinding);
  const confirmedFindings = cortexData.conditionsEvaluated.filter(c => !isMaterialFinding(c));
  const useExpertMode = engineMat.length > 0;

  const hasResults = nodeResults && (
    cortexData.conditionsEvaluated.length > 0
    || cortexData.drgImpact.current_drg
  );

  return (
    <div className="cdi-demo">
      <header className="cdi-demo__header">
        <div className="cdi-demo__header-left">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
          <h1>Clinical Documentation Integrity</h1>
          <span className="cdi-demo__badge">AI-Powered Analysis</span>
        </div>
        <div className="cdi-demo__header-right">
          <span className="cdi-demo__powered">Powered by CerebralMachines</span>
        </div>
      </header>

      <div className="cdi-demo__body">
        {/* LEFT PANEL */}
        <div className="cdi-demo__left">
          <div className="cdi-demo__input-header">
            <h2>Clinical Note Input</h2>
            <p>Paste or type a clinical case note for CDI analysis</p>
          </div>

          <textarea
            className="cdi-demo__textarea"
            value={caseText}
            onChange={(e) => setCaseText(e.target.value)}
            placeholder={"Paste clinical note here...\n\nInclude admission history, lab results, imaging, progress notes, discharge summary, and coded conditions for best results."}
            spellCheck={false}
          />

          <div className="cdi-demo__sample-cases">
            <span className="cdi-demo__sample-label">Quick Load:</span>
            {Object.entries(SAMPLE_CASES).map(([key, c]) => (
              <button key={key} className="cdi-demo__sample-btn" onClick={() => loadSampleCase(key)} disabled={executing} title={c.description}>
                {c.label}
              </button>
            ))}
          </div>

          <div className="cdi-demo__fields">
            <div className="cdi-demo__field">
              <label>Current DRG</label>
              <input type="text" value={currentDrg} onChange={(e) => setCurrentDrg(e.target.value)} placeholder="e.g. 312" disabled={executing} />
            </div>
            <div className="cdi-demo__field">
              <label>Coded Conditions</label>
              <input type="text" value={codedConditions} onChange={(e) => setCodedConditions(e.target.value)} placeholder="e.g. R55, I10, D64.9" disabled={executing} />
            </div>
            <div className="cdi-demo__field">
              <label>Base Rate ($)</label>
              <input type="number" value={facilityBaseRate} onChange={(e) => setFacilityBaseRate(e.target.value)} disabled={executing} />
            </div>
            <div className="cdi-demo__field">
              <label>Admission</label>
              <input type="date" value={admissionDate} onChange={(e) => setAdmissionDate(e.target.value)} disabled={executing} />
            </div>
            <div className="cdi-demo__field">
              <label>Discharge</label>
              <input type="date" value={dischargeDate} onChange={(e) => setDischargeDate(e.target.value)} disabled={executing} />
            </div>
          </div>

          <button className="cdi-demo__analyze-btn" onClick={handleAnalyze} disabled={executing || !caseText.trim()}>
            {executing ? (<><span className="cdi-demo__spinner" />Analyzing...</>) : (
              <><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>Analyze Case</>
            )}
          </button>

          {executing && (
            <div className="cdi-demo__progress">
              <div className="cdi-demo__progress-bar">
                <div className="cdi-demo__progress-fill" style={{ width: `${totalNodes > 0 ? (completedNodes.length / totalNodes) * 100 : 0}%` }} />
              </div>
              <span className="cdi-demo__progress-label">{getProgressLabel()}</span>
              <span className="cdi-demo__progress-timer">{elapsed}s</span>
            </div>
          )}

          {error && <div className="cdi-demo__error">{error}</div>}
        </div>

        {/* RIGHT PANEL */}
        <div className="cdi-demo__right">
          {!nodeResults && !executing && (
            <div className="cdi-demo__empty">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="1.5">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                <rect x="9" y="3" width="6" height="4" rx="1" />
                <line x1="9" y1="12" x2="15" y2="12" />
                <line x1="9" y1="16" x2="13" y2="16" />
              </svg>
              <h3>Ready for Analysis</h3>
              <p>Paste a clinical note on the left and click <strong>Analyze Case</strong> to begin.</p>
              <p className="cdi-demo__empty-sub">
                The platform evaluates cases across 27 clinical knowledge graphs,
                14 deterministic capability engines, and dynamic cortex process assembly
                to produce CDI findings with full explainability.
              </p>
            </div>
          )}

          {executing && !nodeResults && (
            <div className="cdi-demo__loading-panel">
              <div className="cdi-demo__loading-pulse" />
              <h3>Analyzing Clinical Documentation</h3>
              <p>Evaluating across multiple clinical knowledge domains...</p>
              <div className="cdi-demo__loading-phases">
                <PhaseIndicator label="Knowledge Graph Evaluation" count={27} done={completedNodes.length >= 27} active={completedNodes.length < 27 && completedNodes.length > 0} />
                <PhaseIndicator label="Cross-Source Reasoning" done={completedNodes.includes('cross_reasoning')} active={completedNodes.length >= 27 && !completedNodes.includes('cross_reasoning')} />
                <PhaseIndicator label="Cortex Activation" done={completedNodes.includes('cortex_activation')} active={completedNodes.includes('cross_reasoning') && !completedNodes.includes('cortex_activation')} />
                <PhaseIndicator label="Capability Engines" done={completedNodes.includes('capability_engines')} active={completedNodes.includes('cortex_activation') && !completedNodes.includes('capability_engines')} />
                <PhaseIndicator label="Case File Assembly" done={completedNodes.includes('case_file_assembly')} active={completedNodes.includes('capability_engines') && !completedNodes.includes('case_file_assembly')} />
              </div>
            </div>
          )}

          {hasResults && (
            <div className="cdi-demo__results">
              {/* TELEMETRY BANNER */}
              <div className="cdi-demo__telemetry">
                <div className="cdi-demo__telemetry-grid">
                  <TelemetryStat label="Response Time" value={`${elapsed}s`} icon="⏱" />
                  <TelemetryStat label="Knowledge Graphs" value={String(cortexData.selectedGraphs.length || Object.keys(cortexData.snnNodes).length || '—')} icon="🧠" />
                  <TelemetryStat label="Capabilities" value={String(cortexData.selectedCaps.length || Object.keys(cortexData.capNodes).length || '—')} icon="⚙" />
                  <TelemetryStat label="Material Findings" value={String(displayFindings.length)} icon="📋" />
                  <TelemetryStat label="Expertise Nodes Fired" value={String(cortexData.firedNodes || '—')} icon="🔥" />
                  {cortexData.totalTokens > 0 && <TelemetryStat label="Tokens Used" value={cortexData.totalTokens.toLocaleString()} icon="🔤" />}
                  {cortexData.totalCost > 0 && <TelemetryStat label="Cost" value={`$${cortexData.totalCost.toFixed(4)}`} icon="💵" />}
                </div>
              </div>

              {/* DRG IMPACT COMPARISON */}
              <DRGComparisonCard
                drgImpact={cortexData.drgImpact}
                expected={caseExpected}
                baseRate={Number(facilityBaseRate) || 9116}
              />

              {/* MATERIAL FINDINGS */}
              {displayFindings.length > 0 && (
                <div className="cdi-demo__findings-section">
                  <h3 className="cdi-demo__section-title">
                    Material CDI Findings
                    <span className="cdi-demo__count-badge">{displayFindings.length}</span>
                  </h3>
                  <p className="cdi-demo__section-desc">
                    Findings that address documentation gaps representing denial risk, compliance risk, or DRG impact.
                  </p>
                  {displayFindings.map((cond, idx) => (
                    <FindingCard
                      key={idx}
                      condition={cond}
                      recommendation={cortexData.recsByCondition[cond.condition]}
                      physicianQuery={cortexData.queryByCondition[cond.condition]}
                      snnNodes={cortexData.snnNodes}
                      governance={cortexData.governance}
                      isExpert={useExpertMode}
                    />
                  ))}
                </div>
              )}

              {/* CONFIRMED CODES (collapsed) */}
              {confirmedFindings.length > 0 && (
                <ConfirmedCodesSection findings={confirmedFindings} />
              )}

              {/* GOVERNANCE GATES */}
              {cortexData.governance.governance_status && (
                <GovernanceCard governance={cortexData.governance} />
              )}

              {/* ACTIVATION & DIAGNOSTICS */}
              <ActivationCard cortexData={cortexData} />

              {/* EXECUTION SUMMARY */}
              <div className="cdi-demo__execution-summary">
                <h3 className="cdi-demo__section-title">Execution Summary</h3>
                <div className="cdi-demo__summary-grid">
                  <div className="cdi-demo__summary-item">
                    <span className="cdi-demo__summary-label">Status</span>
                    <span className={`cdi-demo__summary-value cdi-demo__status-${statusData?.status}`}>
                      {(statusData?.status || '').toUpperCase()}
                    </span>
                  </div>
                  <div className="cdi-demo__summary-item">
                    <span className="cdi-demo__summary-label">Conditions Evaluated</span>
                    <span className="cdi-demo__summary-value">{cortexData.conditionsEvaluated.length} total ({displayFindings.length} material)</span>
                  </div>
                  <div className="cdi-demo__summary-item">
                    <span className="cdi-demo__summary-label">Nodes Completed</span>
                    <span className="cdi-demo__summary-value">{completedNodes.length} / {totalNodes}</span>
                  </div>
                  <div className="cdi-demo__summary-item">
                    <span className="cdi-demo__summary-label">Execution ID</span>
                    <span className="cdi-demo__summary-value cdi-demo__mono" title={executionId} style={{fontSize: '11px', wordBreak: 'break-all'}}>{executionId}</span>
                  </div>
                  {statusData?.completed_at && statusData?.started_at && (
                    <div className="cdi-demo__summary-item">
                      <span className="cdi-demo__summary-label">Server Duration</span>
                      <span className="cdi-demo__summary-value">
                        {formatDuration(new Date(statusData.completed_at) - new Date(statusData.started_at))}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Fallback: results came back but no conditions found */}
          {nodeResults && !hasResults && !executing && (
            <div className="cdi-demo__empty">
              <h3>Execution Complete</h3>
              <p>The process completed but no structured conditions were returned.</p>
              {executionId && <p className="cdi-demo__mono" style={{fontSize: 12, marginTop: 8}}>Execution: {executionId}</p>}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function formatDuration(ms) {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function PhaseIndicator({ label, count, done, active }) {
  return (
    <div className={`cdi-demo__phase ${done ? 'done' : ''} ${active ? 'active' : ''}`}>
      <span className="cdi-demo__phase-icon">{done ? '✓' : active ? '●' : '○'}</span>
      <span className="cdi-demo__phase-label">{label}</span>
      {count && <span className="cdi-demo__phase-count">({count})</span>}
    </div>
  );
}

function TelemetryStat({ label, value, icon }) {
  return (
    <div className="cdi-demo__tstat">
      <span className="cdi-demo__tstat-icon">{icon}</span>
      <span className="cdi-demo__tstat-value">{value}</span>
      <span className="cdi-demo__tstat-label">{label}</span>
    </div>
  );
}

function DRGComparisonCard({ drgImpact, expected, baseRate }) {
  const pre = expected?.pre_review || {};
  const post = expected?.post_review || {};
  const pdx = expected?.pdx_change || {};

  const preWeight = Number(pre.weight || drgImpact.pre_review?.relative_weight || 0);
  const postWeight = Number(post.weight || drgImpact.post_review?.relative_weight || 0);
  const preDrg = pre.drg || drgImpact.current_drg || drgImpact.drg || '';
  const postDrg = post.drg || drgImpact.post_review?.drg || '';
  const preTitle = pre.title || drgImpact.drg_title || '';
  const postTitle = post.title || drgImpact.post_review?.drg_title || '';
  const prePayment = preWeight ? Math.round(preWeight * baseRate) : null;
  const postPayment = postWeight ? Math.round(postWeight * baseRate) : null;
  const weightDelta = postWeight && preWeight ? postWeight - preWeight : null;
  const paymentDelta = postPayment && prePayment ? postPayment - prePayment : null;

  const hasPdx = pdx.from && pdx.to;
  const hasComparison = preDrg && postDrg;

  if (!preDrg && !postDrg) return null;

  return (
    <div className="cdi-demo__drg-card">
      <h3 className="cdi-demo__section-title">DRG Impact Analysis</h3>

      {hasPdx && (
        <div className="cdi-demo__pdx-change">
          <span className="cdi-demo__pdx-label">Principal Diagnosis Change</span>
          <div className="cdi-demo__pdx-arrow">
            <div className="cdi-demo__pdx-from">
              <span className="cdi-demo__pdx-code">{pdx.from}</span>
              {pdx.from_desc && <span className="cdi-demo__pdx-desc">{pdx.from_desc}</span>}
            </div>
            <span className="cdi-demo__pdx-arrow-icon">→</span>
            <div className="cdi-demo__pdx-to">
              <span className="cdi-demo__pdx-code">{pdx.to}</span>
              {pdx.to_desc && <span className="cdi-demo__pdx-desc">{pdx.to_desc}</span>}
            </div>
          </div>
        </div>
      )}

      {hasComparison ? (
        <div className="cdi-demo__drg-comparison">
          <div className="cdi-demo__drg-col cdi-demo__drg-col-pre">
            <span className="cdi-demo__drg-col-label">Pre-Review</span>
            <div className="cdi-demo__drg-code-big">{preDrg}</div>
            {preTitle && <div className="cdi-demo__drg-title">{preTitle}</div>}
            {preWeight > 0 && <div className="cdi-demo__drg-detail"><span>Weight</span><span>{preWeight.toFixed(4)}</span></div>}
            {prePayment && <div className="cdi-demo__drg-detail"><span>Est. Payment</span><span>${prePayment.toLocaleString()}</span></div>}
          </div>
          <div className="cdi-demo__drg-delta">
            {weightDelta !== null && (
              <div className={`cdi-demo__drg-delta-value ${weightDelta > 0 ? 'positive' : weightDelta < 0 ? 'negative' : ''}`}>
                <span className="cdi-demo__drg-delta-label">Δ Weight</span>
                <span>{weightDelta > 0 ? '+' : ''}{weightDelta.toFixed(4)}</span>
              </div>
            )}
            {paymentDelta !== null && (
              <div className={`cdi-demo__drg-delta-value ${paymentDelta > 0 ? 'positive' : paymentDelta < 0 ? 'negative' : ''}`}>
                <span className="cdi-demo__drg-delta-label">Δ Payment</span>
                <span>{paymentDelta > 0 ? '+' : ''}${Math.abs(paymentDelta).toLocaleString()}</span>
              </div>
            )}
          </div>
          <div className="cdi-demo__drg-col cdi-demo__drg-col-post">
            <span className="cdi-demo__drg-col-label">Post-Review</span>
            <div className="cdi-demo__drg-code-big">{postDrg}</div>
            {postTitle && <div className="cdi-demo__drg-title">{postTitle}</div>}
            {postWeight > 0 && <div className="cdi-demo__drg-detail"><span>Weight</span><span>{postWeight.toFixed(4)}</span></div>}
            {postPayment && <div className="cdi-demo__drg-detail"><span>Est. Payment</span><span className="cdi-demo__drg-pay">${postPayment.toLocaleString()}</span></div>}
          </div>
        </div>
      ) : (
        <div className="cdi-demo__drg-main">
          <div className="cdi-demo__drg-code-big">{preDrg || postDrg}</div>
          {(preTitle || postTitle) && <div className="cdi-demo__drg-title">{preTitle || postTitle}</div>}
        </div>
      )}

      {expected?.codes_added?.length > 0 && (
        <div className="cdi-demo__code-changes">
          <div className="cdi-demo__code-group">
            <span className="cdi-demo__code-group-label cdi-demo__code-add">Codes to Add</span>
            <div className="cdi-demo__code-tags">
              {expected.codes_added.map((c, i) => <span key={i} className="cdi-demo__code-tag cdi-demo__code-tag-add">{c}</span>)}
            </div>
          </div>
          {expected?.codes_deleted?.length > 0 && (
            <div className="cdi-demo__code-group">
              <span className="cdi-demo__code-group-label cdi-demo__code-del">Codes to Delete</span>
              <div className="cdi-demo__code-tags">
                {expected.codes_deleted.map((c, i) => <span key={i} className="cdi-demo__code-tag cdi-demo__code-tag-del">{c}</span>)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConfirmedCodesSection({ findings }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="cdi-demo__confirmed-section">
      <div className="cdi-demo__confirmed-header" onClick={() => setExpanded(!expanded)}>
        <h3 className="cdi-demo__section-title" style={{ margin: 0 }}>
          Confirmed Codes
          <span className="cdi-demo__count-badge cdi-demo__count-badge-muted">{findings.length}</span>
        </h3>
        <span className="cdi-demo__confirmed-hint">No action required</span>
        <span className="cdi-demo__finding-expand">{expanded ? '▲' : '▼'}</span>
      </div>
      {expanded && (
        <div className="cdi-demo__confirmed-list">
          {findings.map((c, i) => (
            <div key={i} className="cdi-demo__confirmed-item">
              <span className="cdi-demo__confirmed-check">✓</span>
              {c.icd10_code && <span className="cdi-demo__finding-code">{c.icd10_code}</span>}
              <span className="cdi-demo__confirmed-name">{c.condition || c.condition_name}</span>
              {c.poa && <span className="cdi-demo__finding-poa">POA: {c.poa}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FindingCard({ condition, recommendation, physicianQuery, snnNodes, governance, isExpert }) {
  const [expanded, setExpanded] = useState(false);

  const name = condition.condition || condition.condition_name || '';
  const code = condition.icd10_code || condition.code || '';
  const qualifier = (condition.qualifier || 'identified').toLowerCase();
  const action = condition.action || '';
  const poa = condition.poa || '';
  const findingType = condition.finding_type || '';

  // Data sources: engine materiality → expert curated → raw engine evidence
  const expertDesc = condition.description || '';
  const matExplanation = condition.materiality_explanation || condition.materiality || '';
  const engineEvidence = condition.clinical_evidence || condition.evidence || '';
  const sourceGraphs = condition.sources || condition.source_graphs || [];
  const whatIfScenarios = condition.what_if_scenarios || [];
  const matTypes = condition.materiality_type_labels || condition.materiality_types || [];
  const ccMccLevel = condition.cc_mcc_level || '';

  const isQuery = findingType === 'query' || action === 'Query' || qualifier === 'possible'
    || condition.query_flag === true || condition.query_or_direct === 'Q';

  const actionLabel = action === 'Retain' ? 'CONFIRMED'
    : action === 'Query' ? 'QUERY'
    : action === 'Add' ? 'ADD'
    : action === 'Delete' ? 'DELETE'
    : action === 'Upgrade' ? 'UPGRADE'
    : isQuery ? 'QUERY'
    : qualifier.toUpperCase();

  const actionColor = action === 'Retain' ? '#16a34a'
    : action === 'Query' ? '#d97706'
    : action === 'Add' ? '#0891b2'
    : action === 'Delete' ? '#dc2626'
    : action === 'Upgrade' ? '#7c3aed'
    : isQuery ? '#d97706'
    : QUALIFIER_COLORS[qualifier] || '#6366f1';

  return (
    <div className={`cdi-demo__finding ${expanded ? 'expanded' : ''}`}>
      <div className="cdi-demo__finding-header" onClick={() => setExpanded(!expanded)}>
        <div className="cdi-demo__finding-main">
          <span className="cdi-demo__finding-action" style={{ backgroundColor: actionColor }}>
            {actionLabel}
          </span>
          {code && <span className="cdi-demo__finding-code">{code}</span>}
          <span className="cdi-demo__finding-name">{name}</span>
        </div>
        <div className="cdi-demo__finding-meta">
          {ccMccLevel && <span className="cdi-demo__finding-cc" title={`${ccMccLevel} classification`}>{ccMccLevel}</span>}
          {poa && <span className="cdi-demo__finding-poa" title="Present on Admission">POA: {poa}</span>}
          <span className="cdi-demo__finding-expand">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>
      {expanded && (
        <div className="cdi-demo__finding-detail">
          {/* Clinical Evidence / Expert Description */}
          <div className="cdi-demo__detail-section">
            <h4>Clinical Evidence</h4>
            <p>{expertDesc || engineEvidence || 'No specific clinical evidence recorded for this finding.'}</p>
          </div>

          {/* Materiality */}
          {matExplanation && (
            <div className="cdi-demo__detail-section cdi-demo__detail-materiality">
              <h4>Materiality</h4>
              <p>{matExplanation}</p>
              {matTypes.length > 0 && (
                <div className="cdi-demo__mat-types">
                  {matTypes.map((t, i) => <span key={i} className="cdi-demo__mat-type-tag">{t}</span>)}
                </div>
              )}
            </div>
          )}

          {/* What-If Scenarios */}
          {whatIfScenarios.length > 1 && (
            <div className="cdi-demo__detail-section">
              <h4>What-If Scenario Analysis</h4>
              <table className="cdi-demo__scenario-table">
                <thead>
                  <tr>
                    <th>Scenario</th>
                    <th>DRG</th>
                    <th>Weight</th>
                    <th>Payment</th>
                    <th>Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {whatIfScenarios.map((s, i) => {
                    const baseline = whatIfScenarios[0];
                    const delta = (s.payment || 0) - (baseline.payment || 0);
                    return (
                      <tr key={i} className={i === 0 ? 'cdi-demo__scenario-baseline' : ''}>
                        <td>{s.description || s.scenario}</td>
                        <td className="cdi-demo__scenario-mono">{s.drg || '—'}</td>
                        <td className="cdi-demo__scenario-mono">{s.weight ? s.weight.toFixed(4) : '—'}</td>
                        <td className="cdi-demo__scenario-mono">{s.payment ? `$${s.payment.toLocaleString()}` : '—'}</td>
                        <td className={`cdi-demo__scenario-delta ${delta > 0 ? 'positive' : delta < 0 ? 'negative' : ''}`}>
                          {i === 0 ? '—' : `${delta >= 0 ? '+' : ''}$${delta.toLocaleString()}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Authoritative Source */}
          {sourceGraphs.length > 0 && (
            <div className="cdi-demo__detail-section">
              <h4>Authoritative Source</h4>
              <div className="cdi-demo__source-list">
                {sourceGraphs.map((g, i) => (
                  <span key={i} className="cdi-demo__source-tag">{GRAPH_DISPLAY_NAME[g] || g}</span>
                ))}
              </div>
            </div>
          )}

          {/* Status */}
          <div className="cdi-demo__detail-section">
            <h4>Status</h4>
            <div className="cdi-demo__status-row">
              <span className="cdi-demo__detail-qual" style={{ borderColor: actionColor, color: actionColor }}>
                {isQuery ? 'Requires Physician Query'
                  : action === 'Retain' ? 'Confirmed — Code Directly'
                  : action === 'Add' ? 'Add to Coding'
                  : action === 'Delete' ? 'Remove from Coding'
                  : action === 'Upgrade' ? 'Upgrade Code Specificity'
                  : qualifier === 'confirmed' ? 'Confirmed — Code Directly'
                  : qualifier}
              </span>
              {poa && (
                <span className="cdi-demo__detail-poa">
                  POA: {poa === 'Y' ? 'Yes — Present on Admission' : poa === 'N' ? 'No — Not Present on Admission' : poa}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GovernanceCard({ governance }) {
  const status = governance.governance_status || '';
  const gates = governance.gates_passed || [];
  const uniqueGates = [];
  const seen = new Set();
  gates.forEach((g) => {
    if (!seen.has(g.gate)) { seen.add(g.gate); uniqueGates.push(g); }
  });

  return (
    <div className="cdi-demo__governance-card">
      <h3 className="cdi-demo__section-title">
        Governance Compliance
        <span className={`cdi-demo__gov-status ${status === 'PASSED' ? 'passed' : 'failed'}`}>
          {status}
        </span>
      </h3>
      <div className="cdi-demo__gates-list">
        {uniqueGates.map((g, i) => (
          <div key={i} className="cdi-demo__gate-item">
            <span className="cdi-demo__gate-check">✓</span>
            <div>
              <div className="cdi-demo__gate-name">{g.gate?.replace(/_/g, ' ')}</div>
              <div className="cdi-demo__gate-desc">{g.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivationCard({ cortexData }) {
  const [expanded, setExpanded] = useState(false);
  const caps = cortexData.rankedCaps || {};
  const sortedCaps = Object.entries(caps).sort((a, b) => b[1] - a[1]);

  return (
    <div className="cdi-demo__activation-card">
      <div className="cdi-demo__activation-header" onClick={() => setExpanded(!expanded)}>
        <h3 className="cdi-demo__section-title" style={{ margin: 0 }}>
          Dynamic Cortex Assembly
        </h3>
        <span className="cdi-demo__finding-expand">{expanded ? '▲' : '▼'}</span>
      </div>
      <div className="cdi-demo__activation-summary-row">
        <div className="cdi-demo__act-stat">
          <span className="cdi-demo__act-val">{cortexData.firedNodes || '—'}</span>
          <span className="cdi-demo__act-lbl">Expertise Nodes Fired</span>
        </div>
        <div className="cdi-demo__act-stat">
          <span className="cdi-demo__act-val">{cortexData.selectedCaps.length || '—'}</span>
          <span className="cdi-demo__act-lbl">Capabilities Selected</span>
        </div>
        <div className="cdi-demo__act-stat">
          <span className="cdi-demo__act-val">{Object.keys(cortexData.snnNodes).length || '—'}</span>
          <span className="cdi-demo__act-lbl">Knowledge Graphs Used</span>
        </div>
      </div>
      {expanded && sortedCaps.length > 0 && (
        <div className="cdi-demo__ranked-caps">
          <h4>Ranked Capabilities (by activation score)</h4>
          {sortedCaps.map(([cap, score]) => (
            <div key={cap} className="cdi-demo__ranked-cap">
              <span className="cdi-demo__cap-name">{cap.replace(/^cap_/, '').replace(/_/g, ' ')}</span>
              <div className="cdi-demo__cap-bar-wrap">
                <div className="cdi-demo__cap-bar" style={{ width: `${Math.min(100, (score / (sortedCaps[0]?.[1] || 1)) * 100)}%` }} />
              </div>
              <span className="cdi-demo__cap-score">{score}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CDIDemoPage;
